import { useEffect, useLayoutEffect, useRef } from 'react';
import type BoardPanelModel from 'app/stores/boards/BoardPanelModel';
import type QueryModel from 'app/stores/ude/QueryModel';
import { usePanelVisibility } from 'app/views/boards/usePanelVisibility';

interface UseBoardPanelLoaderOptions {
  panel: BoardPanelModel;
  queryModel: QueryModel;
  isInBoardView: boolean;
}

export function useBoardPanelLoader({ panel, queryModel, isInBoardView }: UseBoardPanelLoaderOptions) {
  const { loadScheduler } = panel.board;
  const panelKey = String(panel.id);

  const { elementRef, getPriority } = usePanelVisibility({
    enabled: isInBoardView,
    observeKey: panelKey,
    onVisibilityChange: (isInView) => loadScheduler.setInView({ id: panelKey, isInView })
  });

  const wasPendingRef = useRef(false);

  useEffect(() => {
    if (!isInBoardView) {
      return undefined;
    }

    const unregister = loadScheduler.register(panelKey, {
      run: () => queryModel.run(),
      cancel: () => queryModel.unsubscribe(),
      isRunning: () => queryModel.isLoading,
      getPriority
    });

    if (wasPendingRef.current) {
      wasPendingRef.current = false;
      loadScheduler.schedule(panelKey);
    }

    return () => {
      // unregister drops the pending run: re-schedule it under the new key, or on remount via dirty
      if (loadScheduler.isPending(panelKey)) {
        wasPendingRef.current = true;
        queryModel.dirty = true;
      }
      unregister();
    };
  }, [isInBoardView, loadScheduler, panelKey, queryModel, getPriority]);

  // layout effect: must be set before UdeDataView's mount effect calls requestRun()
  useLayoutEffect(() => {
    queryModel.requestRun = isInBoardView ? () => loadScheduler.schedule(panelKey) : () => queryModel.run();
  }, [isInBoardView, queryModel, loadScheduler, panelKey]);

  return {
    elementRef,
    isPending: isInBoardView && loadScheduler.isPending(panelKey)
  };
}
